import React, { useContext } from 'react';
import { Context } from '../../context/news_search.context';
import NoData from '../../../components/react/no_data/no_data';
import { Article, Author, CompanyOrTopic } from '../../types/news_search.types';
import { NEWS_AUTHOR_PATH, NEWS_PATH } from '../../constants/news_search.constants';
import styles from './articles.module.scss';
import { dateToString } from '../../../utilities/utilities';
const CLOUDINARY_BASE_PATH =
  'https://res.cloudinary.com/devex/image/fetch/c_fill,f_auto,q_auto,w_416,h_234';

const Articles: React.FC = () => {
  const { articles, setPageNumber, setQuery, setQueryInput, topics, setTopics } =
    useContext(Context);

  const renderNoData: JSX.Element = (
    <div className='col-xs-12'>
      <NoData large textDescription='No articles found' />
    </div>
  );

  const onQueryChange = (query: string): void => {
    setPageNumber(1);
    setQuery(query);
    setQueryInput(query);
    window.scrollTo({
      top: document.getElementById('results').offsetTop,
      behavior: 'smooth',
    });
  };

  const onTopicChange = (topicName: string): void => {
    if (!(topics || []).includes(topicName)) {
      setPageNumber(1);
      setTopics([...(topics || []), topicName]);
      window.scrollTo({
        top: document.getElementById('results').offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const renderResults: JSX.Element[] = articles.map(
    (article: Article, index: number): JSX.Element => (
      <div key={index} className={`row ${index > 0 ? 'py-48' : 'pt-1 pb-48'} border-bottom`}>
        <div className='col-sm-4 d-none d-md-block'>
          {article.thumbnail && (
            <div className='relative'>
              {article.overline && (
                <div className={`${styles.overlayedLabelForOverline} text-end`}>
                  {article.overline_link_target ? (
                    <a
                      className='text-white'
                      href={article.overline_link_target}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {article.overline}
                    </a>
                  ) : (
                    <a className='text-white' onClick={() => onQueryChange(article.overline)}>
                      {article.overline}
                    </a>
                  )}
                </div>
              )}
              <figure>
                <img
                  src={`${CLOUDINARY_BASE_PATH}/${article.thumbnail}`}
                  className='w-100'
                  alt={`${article.title} thumbnail`}
                />
              </figure>
            </div>
          )}
        </div>
        <div className='col-sm-8 col-12'>
          {article.overline && (
            <div className={`d-md-none pb-2 ${styles.mobileOverlineLink}`}>
              {article.overline_link_target ? (
                <a
                  className='text-orange-dark fs-4'
                  href={article.overline_link_target}
                  target='_blank'
                  rel='noreferrer'
                >
                  {article.overline}
                </a>
              ) : (
                <a
                  className='text-orange-dark fs-4'
                  onClick={() => onQueryChange(article.overline)}
                >
                  {article.overline}
                </a>
              )}
            </div>
          )}
          <a
            className={`link-gray-blue ${styles.articleName}`}
            href={`${NEWS_PATH}/${article.slug_and_id}`}
          >
            {article.title}
          </a>
          <div className={`py-25 ${styles.articleAuthor}`}>
            {!!article.authors.length && (
              <>
                <span>
                  By{' '}
                  {article.authors.map((author: Author, idx: number) => (
                    <span key={idx}>
                      {author.full_name && (
                        <a
                          className={`fw-bold ${styles.authorLink}`}
                          href={`${NEWS_AUTHOR_PATH}/${author.id}`}
                        >
                          {author.full_name}
                        </a>
                      )}
                      {idx + 1 < article.authors.length && ', '}
                    </span>
                  ))}
                </span>
              </>
            )}
            {article.publication_date && !article.is_evergreen && (
              <>
                {!!article.authors.length && <span className='px-2 small'>{'//'}</span>}
                <span>{dateToString(new Date(article.publication_date), false)}</span>
              </>
            )}
          </div>
          <p className={`mb-1 ${styles.articleTeaser}`}>{article.teaser}</p>
          {(!!article.news_topics.length ||
            !!article.companies.length ||
            !!article.tags.length) && (
            <>
              <span data-testid='article-news-topics'>
                {article.news_topics.map((topic: CompanyOrTopic, idx: number) => (
                  <span
                    key={idx}
                    className='btn btn-outline-info btn-sm mt-25 me-25'
                    onClick={() => onTopicChange(topic.name)}
                  >
                    {topic.name}
                  </span>
                ))}
              </span>
              {article.companies.map((company: CompanyOrTopic, idx: number) => (
                <span
                  key={idx}
                  className='btn btn-outline-info btn-sm mt-25 me-25'
                  onClick={() => onQueryChange(company.name)}
                >
                  {company.name}
                </span>
              ))}
              <span data-testid='article-tags'>
                {article.tags.map((tag: string, idx: number) => (
                  <span
                    key={idx}
                    className='btn btn-outline-info btn-sm mt-25 me-25'
                    onClick={() => onQueryChange(tag)}
                  >
                    {tag}
                  </span>
                ))}
              </span>
            </>
          )}
        </div>
      </div>
    )
  );

  return (
    <div className='container mt-4'>
      <div className='row pb-25 mb-4'>
        <div className='col-xs-12'>{articles.length ? renderResults : renderNoData}</div>
      </div>
    </div>
  );
};

export default Articles;
